<template>
  <div class="messaging-sidebar">
    <!-- Left: Conversation List -->
    <div v-if="!isCreating" class="sidebar-left">
      <div v-if="isLoading" class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Loading conversations...</span>
      </div>
      <div v-else-if="error" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
      <div v-else-if="conversations.length === 0" class="empty-state">
        <i class="pi pi-inbox"></i>
        <span>No threads found</span>
        <Button 
          label="Start New Thread" 
          class="p-button-text" 
          @click="$emit('update:isCreating', true)"
        />
      </div>
      <ul v-else class="conversation-list">
        <li
          v-for="conv in conversations"
          :key="conv.id"
          :class="{ 
            active: selectedConversation && selectedConversation.id === conv.id,
            'has-unread': conv.unreadMessages > 0,
            'is-closed': conv.isClosed
          }"
          @click="selectConversation(conv)"
        >
          <div class="conversation-header">
            <div class="conversation-title">{{ truncateText(conv.subject) }}</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Right: Chat or New Message -->
    <div class="sidebar-right">
      <div class="chat-container" v-if="!isCreating && selectedConversation">
        <!-- Chat view -->
        <div v-if="isLoadingMessages" class="messages-loading-state">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Loading messages...</span>
        </div>
        <div class="chat-history" v-show="!isLoadingMessages">
          <div v-for="msg in selectedConversation.messages" :key="msg.id" 
               class="chat-message"
               :class="{ 'unread': !msg.isRead }">
            <div class="chat-header">
              <div class="chat-author">{{ msg.author }}</div>
              <div class="chat-time">{{ formatDate(msg.createdOn) }}</div>
            </div>
            <div class="chat-text" v-html="msg.text"></div>
            <div v-if="msg.files && msg.files.length > 0" class="chat-files">
              <div v-for="file in msg.files" :key="file.attachment_id" class="file-item" @click="downloadFile(file.attachment_id, file.file_name)">
                <i class="pi pi-file"></i>
                <span class="file-name">{{ file.file_name }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="chat-input" v-if="!isLoadingMessages">
          <div class="input-container">
            <QuillEditor ref="chatQuillEditor" v-model="newMessage" placeholder="Type..." class="chat-input-box" height="160px" />
            <div class="selected-files" v-if="selectedFiles.length > 0">
            <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
              <div class="file-info">
                <i class="pi pi-file"></i>
                <span class="file-name">{{ file.name }}</span>
                <div class="file-loader" v-if="file.uploading">
                  <i class="pi pi-spin pi-spinner"></i>
                </div>
              </div>
              <Button 
                icon="pi pi-times" 
                class="p-button-text p-button-rounded remove-file-button" 
                @click="removeFile(index)"
              />
            </div>
          </div>
            <div class="input-actions">
              <div class="input-actions-left">
                <FileUpload 
                  mode="basic" 
                  name="demo[]" 
                  url="/api/upload" 
                  :maxFileSize="1000000" 
                  :auto="true" 
                  chooseLabel="Upload"
                  @select="onFileSelect"
                  @remove="onFileRemove"
                />
              </div>
              <div class="input-actions-right">
                <Button label="Send" @click="sendMessage" :disabled="!newMessage.trim()" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="isCreating">
        <!-- New message form -->
        <div class="new-message-container">
          <div class="form-group">
            <label>Subject</label>
            <InputText v-model="newSubject" placeholder="Enter message subject" class="form-input" />
          </div>
          <div class="form-group">
            <label>Message</label>
            <QuillEditor ref="newThreadQuillEditor" v-model="newMessage" placeholder="Type your message here..." class="form-input" height="150px" />
          </div>
          
          <!-- File upload section - same style as chat input -->
          <div class="selected-files" v-if="newThreadFiles.length > 0">
            <div v-for="(file, index) in newThreadFiles" :key="index" class="file-item">
              <div class="file-info">
                <i class="pi pi-file"></i>
                <span class="file-name">{{ file.name }}</span>
              </div>
              <Button 
                icon="pi pi-times" 
                class="p-button-text p-button-rounded remove-file-button" 
                @click="removeNewThreadFile(index)"
              />
            </div>
          </div>
          
          <div class="form-actions">
              <FileUpload 
              mode="basic" 
              name="demo[]" 
              url="/api/upload" 
              :maxFileSize="1000000" 
              :auto="true" 
              chooseLabel="Upload"
              @select="onNewThreadFileSelect"
              @remove="onNewThreadFileRemove"
            />
            <div class="send-actions">
              <Button label="Cancel" class="p-button-text" @click="cancelNewMessage" />
              <Button label="Send" @click="sendNewMessage" :disabled="!newSubject.trim() || !newMessage.trim()" />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="empty-chat">Select a conversation to view messages.</div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import QuillEditor from '../common/QuillEditor.vue';
import MessagingService from '../../service/MessagingService';

export default {
  name: 'Messaging',
  components: { Button, InputText, FileUpload, QuillEditor },
  props: {
    message: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false
    },
    encryptionId: {
      type: String,
      default: null
    }
  },
  emits: ['update:isCreating', 'close', 'message-sent', 'new-conversation'],
  data() {
    return {
      conversations: [],
      selectedConversation: null,
      newSubject: '',
      newMessage: '',
      selectedFiles: [],
      messagingService: new MessagingService(),
      isLoading: true,
      isLoadingMessages: false,
      error: null,
      newThreadFiles: []
    };
  },
  watch: {
    isCreating(newVal) {
      if (!newVal) {
        this.newSubject = '';
        this.newMessage = '';
        this.newThreadFiles = [];
      }
    }
  },
  mounted() {
    this.loadThreads();
  },
  methods: {
    async loadThreads() {
      try {
        this.isLoading = true;
        this.error = null;
        const response = await this.messagingService.getThreadList(this.encryptionId);

        console.log("Conversations  >>", response);

        
        if (response.data.success && response.data.data) {
          this.conversations = response.data.data.map(thread => ({
            id: thread.id,
            title: thread.subject,
            preview: thread.buyerName,
            subject: thread.subject,
            status: thread.status,
            vendorName: thread.vendorName,
            buyerName: thread.buyerName,
            totalMessages: thread.totalMessages,
            unreadMessages: thread.unreadMessages,
            isClosed: thread.isClosed,
            createdOn: thread.createdOn,
            modifiedOn: thread.modifiedOn
          }));


          if (this.conversations.length > 0) {
            this.selectedConversation = this.conversations[0];
            await this.loadMessages(this.selectedConversation.id);
          }
        } else if (!response.data.success && response.data.errorMessage) {
          this.error = response.errorMessage;
        }
      } catch (err) {
        console.error('Error loading threads:', err);
        this.error = 'An error occurred while loading conversations';
      } finally {
        this.isLoading = false;
      }
    },

    async loadMessages(threadId) {
      try {
        this.isLoadingMessages = true;
        const response = await this.messagingService.getThreadMessages(threadId);
        if (response.data.success) {
          const messages = response.data.data.messages;
          // Sort messages by createdOn in ascending order
          messages.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
          
          this.selectedConversation.messages = messages.map(msg => ({
            id: msg.message_id,
            author: msg.from,
            text: msg.message,
            time: msg.header,
            senderType: msg.sender_type,
            isRead: msg.is_read,
            files: msg.files,
            createdOn: msg.createdOn
          }));
        }
      } catch (err) {
        console.error('Error loading messages:', err);
      } finally {
        this.isLoadingMessages = false;
      }
    },

    async selectConversation(conv) {
      this.selectedConversation = conv;
      this.$emit('update:isCreating', false);
      this.newMessage = '';
      // Clear the Quill editor using setContent
      this.$nextTick(() => {
        if (this.$refs.chatQuillEditor && this.$refs.chatQuillEditor.setContent) {
          this.$refs.chatQuillEditor.setContent('');
        }
      });
      await this.loadMessages(conv.id);
    },

    async sendMessage() {
      if (this.newMessage.trim() && this.selectedConversation) {
        try {
          let attachments = [];
          
          // Process file uploads if there are selected files
          if (this.selectedFiles.length > 0) {
            for (const fileObj of this.selectedFiles) {
              const file = fileObj.file;
              
              // Set uploading state for this file
              const fileIndex = this.selectedFiles.findIndex(f => f.name === fileObj.name);
              if (fileIndex !== -1) {
                this.selectedFiles[fileIndex].uploading = true;
              }
              
              // 1. Get presigned URL
              console.log('Getting presigned URL for file:', file.name);
              const presignedResp = await this.messagingService.getPresignedUrlForAttachment(file.name);
              console.log('Presigned response:', presignedResp);
              if (!presignedResp.data || !presignedResp.data.presignedUrl) {
                this.error = 'Failed to get upload URL for ' + file.name;
                // Reset uploading state
                if (fileIndex !== -1) {
                  this.selectedFiles[fileIndex].uploading = false;
                }
                return;
              }
              const { presignedUrl, filePath, fileName, uniqueFileName } = presignedResp.data;
              
              // 2. Upload file to presigned URL
              console.log('Uploading file to presigned URL:', presignedUrl);
              const uploadResp = await fetch(presignedUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': file.type
                },
                body: file
              });
              console.log('Upload response:', uploadResp);
              if (!uploadResp.ok) {
                this.error = 'Failed to upload file: ' + file.name;
                // Reset uploading state
                if (fileIndex !== -1) {
                  this.selectedFiles[fileIndex].uploading = false;
                }
                return;
              }
              
              // 3. Collect attachment metadata
              attachments.push({
                fileName: fileName,
                filePath: filePath,
                fileSize: file.size,
                mimeType: file.type
              });
              
              // Reset uploading state for this file
              if (fileIndex !== -1) {
                this.selectedFiles[fileIndex].uploading = false;
              }
            }
          }

          // 4. Send message with attachments
          console.log('Sending message with attachments:', attachments);
          const response = await this.messagingService.addMessageToThread(
            this.selectedConversation.id,
            this.newMessage.trim(),
            this.encryptionId,
            attachments
          );

          if (response.data.success) {
            await this.loadMessages(this.selectedConversation.id);
            this.newMessage = '';
            this.selectedFiles = []; // Clear selected files after successful send
            // Clear the Quill editor using setContent
            this.$nextTick(() => {
              if (this.$refs.chatQuillEditor && this.$refs.chatQuillEditor.setContent) {
                this.$refs.chatQuillEditor.setContent('');
              }
            });
          } else {
            this.error = response.data.errorMessage || 'Failed to send message';
          }
        } catch (err) {
          this.error = 'An error occurred while sending the message';
          console.error('Error sending message:', err);
          // Reset uploading state for all files on error
          this.selectedFiles.forEach(file => {
            file.uploading = false;
          });
        }
      }
    },

    async sendNewMessage() {
      console.log('Sending new message with attachments:', this.newThreadFiles);
      if (this.newSubject.trim() && this.newMessage.trim()) {
        try {
          let attachments = [];
          // Only process if there are files
          if (this.newThreadFiles.length > 0) {
            for (const fileObj of this.newThreadFiles) {
              const file = fileObj.file;
              // 1. Get presigned URL
              console.log('Getting presigned URL for file:', file.name);
              const presignedResp = await this.messagingService.getPresignedUrlForAttachment(file.name);
              console.log('Presigned response:', presignedResp);
              if (!presignedResp.data || !presignedResp.data.presignedUrl) {
                this.error = 'Failed to get upload URL for ' + file.name;
                return;
              }
              const { presignedUrl, filePath, fileName, uniqueFileName } = presignedResp.data;
              // 2. Upload file to presigned URL
              console.log('Uploading file to presigned URL:', presignedUrl);
              const uploadResp = await fetch(presignedUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': file.type
                },
                body: file
              });
              console.log('Upload response:', uploadResp);
              if (!uploadResp.ok) {
                this.error = 'Failed to upload file: ' + file.name;
                return;
              }
              // 3. Collect attachment metadata
              attachments.push({
                fileName: fileName,
                filePath: filePath,
                fileSize: file.size,
                mimeType: file.type
              });
            }
          }

          // 4. Create thread with attachments
          console.log('Creating thread with attachments:', attachments);
          const response = await this.messagingService.createNewThread(
            this.encryptionId,
            this.newSubject.trim(),
            this.newMessage.trim(),
            attachments
          );
          console.log('Thread creation response:', response);

          if (response.data.success) {
            // Create new conversation object with the response data
            const newConv = {
              id: response.data.data.threadId,
              subject: this.newSubject.trim(),
              title: this.newSubject.trim(),
              buyerName: 'New Thread', // This will be updated when we load the thread details
              vendorName: '', // This will be updated when we load the thread details
              totalMessages: 1,
              unreadMessages: 0,
              isClosed: false,
              createdOn: new Date().toISOString(),
              modifiedOn: new Date().toISOString(),
              messages: []
            };
            // Reset form first
            this.newSubject = '';
            this.newMessage = '';
            this.newThreadFiles = [];
            // Clear the new thread Quill editor using setContent
            this.$nextTick(() => {
              if (this.$refs.newThreadQuillEditor && this.$refs.newThreadQuillEditor.setContent) {
                this.$refs.newThreadQuillEditor.setContent('');
              }
            });
            // Add the new conversation to the list and select it
            this.conversations.unshift(newConv);
            this.selectedConversation = newConv;
            // Load messages for the new thread
            await this.loadMessages(newConv.id);
            // Update UI state after everything is loaded
            this.$emit('update:isCreating', false);
            this.$emit('new-conversation', newConv);
          } else {
            this.error = response.data.errorMessage || 'Failed to create new conversation';
          }
        } catch (err) {
          this.error = 'An error occurred while creating the conversation';
          console.error('Error creating conversation:', err);
        }
      }
    },
    cancelNewMessage() {
      this.$emit('update:isCreating', false);
      this.selectedConversation = this.conversations[0];
      this.newSubject = '';
      this.newMessage = '';
      this.newThreadFiles = [];
    },
    onFileSelect(event) {
      const files = event.files;
      files.forEach(file => {
        this.selectedFiles.push({
          name: file.name,
          file: file,
          uploading: false
        });
      });
    },
    onFileRemove(event) {
      const index = this.selectedFiles.findIndex(f => f.name === event.file.name);
      if (index !== -1) {
        this.selectedFiles.splice(index, 1);
      }
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    formatDate(date) {
      const formattedDate = new Date(date).toLocaleDateString();
      return formattedDate;
    },
    truncateText(text, maxLength = 32) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
      }
      return text;
    },
    onNewThreadFileSelect(event) {
      const files = event.files;
      files.forEach(file => {
        this.newThreadFiles.push({
          name: file.name,
          file: file
        });
      });
    },
    onNewThreadFileRemove(event) {
      const index = this.newThreadFiles.findIndex(f => f.name === event.file.name);
      if (index !== -1) {
        this.newThreadFiles.splice(index, 1);
      }
    },
    removeNewThreadFile(index) {
      this.newThreadFiles.splice(index, 1);
    },
    async downloadFile(fileId, fileName) {
      try {
        const response = await this.messagingService.getFileDownloadUrl(fileId);
        if (response.data.success && response.data.data) {
          const { downloadUrl, fileName: actualFileName } = response.data.data;
          
          // Fetch the file as a blob to force download
          const fileResponse = await fetch(downloadUrl);
          if (!fileResponse.ok) {
            throw new Error('Failed to fetch file');
          }
          
          const blob = await fileResponse.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Create a temporary link element to trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = actualFileName || fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the object URL
          window.URL.revokeObjectURL(url);
        } else {
          this.error = 'Failed to get download URL for ' + fileName;
        }
      } catch (err) {
        this.error = 'Failed to download file: ' + fileName;
        console.error('Error downloading file:', err);
      }
    }
  }
};
</script>

<style >
.messaging-sidebar {
  display: flex;
  height: 100%;
}

.sidebar-left {
  width: 30%;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  padding-right: 10.5px;
  padding-top: 10.5px;
}

.conversation-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.conversation-list li {
  padding: 1rem;
  cursor: pointer;
  border-radius: 8px;
}

.conversation-list li:hover {
  background: #f8f9fa;
}

.conversation-list li.active {
  background: #245B5F;
  color: #fff;
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.conversation-title {
  font-weight: 600;
}

.unread-badge {
  background: #245B5F;
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
  min-width: 20px;
  text-align: center;
}

.conversation-preview {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.conversation-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #999;
}

.status {
  text-transform: capitalize;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.status.active {
  background: #e8f5e9;
  color: #2e7d32;
}

.status.closed {
  background: #ffebee;
  color: #c62828;
}

/* .conversation-list li.has-unread {
  background: #f8f9fa;
}

.conversation-list li.is-closed {
  opacity: 0.7;
} */

.sidebar-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  height: 100%;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background-color: #FCFCFC;
}

.chat-message {
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #FFF;
  border: 1px solid #E1E5EA;
}


.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.chat-author {
  font-weight: 600;
  color: #245B5F;
}

.chat-time {
  font-size: 0.75rem;
  color: #666;
}

.chat-text {
  margin: 0.5rem 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* HTML Content Styling */
.chat-text h1 {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0.5rem 0;
  color: #333;
}

.chat-text h2 {
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0.5rem 0;
  color: #333;
}

.chat-text h3 {
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0.5rem 0;
  color: #333;
}

.chat-text div {
  margin: 0.25rem 0;
  line-height: 1.4;
}

.chat-text p {
  margin: 0.25rem 0;
  line-height: 1.4;
}

.chat-text strong {
  font-weight: bold;
}

.chat-text em {
  font-style: italic;
}

.chat-text u {
  text-decoration: underline;
}

.chat-text s {
  text-decoration: line-through;
}

/* Quill Editor Font Classes */
.chat-text .ql-font-serif {
  font-family: Georgia, Times, "Times New Roman", serif;
}

.chat-text .ql-font-monospace {
  font-family: "Courier New", Courier, monospace;
}

/* Quill Editor Size Classes */
.chat-text .ql-size-small {
  font-size: 0.75rem;
}

.chat-text .ql-size-large {
  font-size: 1.25rem;
}

.chat-text .ql-size-huge {
  font-size: 1.5rem;
}

/* Subscript and Superscript */
.chat-text sub {
  vertical-align: sub;
  font-size: 0.75em;
}

.chat-text sup {
  vertical-align: super;
  font-size: 0.75em;
}

/* Lists */
.chat-text ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.chat-text ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.chat-text li {
  margin: 0.25rem 0;
  line-height: 1.4;
}

/* Text Alignment */
.chat-text .ql-align-left {
  text-align: left;
}

.chat-text .ql-align-center {
  text-align: center;
}

.chat-text .ql-align-right {
  text-align: right;
}

.chat-text .ql-align-justify {
  text-align: justify;
}

/* Indentation */
.chat-text .ql-indent-1 {
  padding-left: 2rem;
}

.chat-text .ql-indent-2 {
  padding-left: 4rem;
}

.chat-text .ql-indent-3 {
  padding-left: 6rem;
}

.chat-text .ql-indent-4 {
  padding-left: 8rem;
}

.chat-text .ql-indent-5 {
  padding-left: 10rem;
}

.chat-text .ql-indent-6 {
  padding-left: 12rem;
}

.chat-text .ql-indent-7 {
  padding-left: 14rem;
}

.chat-text .ql-indent-8 {
  padding-left: 16rem;
}

.chat-text img {
  max-width: 100%;
  height: auto;
}

/* Blockquotes */
.chat-text blockquote {
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;
  border-left: 4px solid #245B5F;
  background-color: #f8f9fa;
  font-style: italic;
  color: #666;
}

/* Code Blocks */
.chat-text pre.ql-syntax {
  background-color: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.75rem;
  margin: 0.5rem 0;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.9rem;
  line-height: 1.4;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Links */
.chat-text a {
  color: #245B5F;
  text-decoration: underline;
  word-break: break-all;
}

.chat-text a:hover {
  color: #1a3f42;
  text-decoration: none;
}

/* Line Breaks */
.chat-text br {
  display: block;
  margin: 0.25rem 0;
}

/* Remove Quill cursor element */
.chat-text .ql-cursor {
  display: none;
}

/* Ensure proper spacing for inline elements */
.chat-text span {
  display: inline;
}

/* Style colored text and backgrounds */
.chat-text span[style*="color"] {
  display: inline;
}

.chat-text span[style*="background-color"] {
  display: inline;
  padding: 0.1rem 0.2rem;
  border-radius: 2px;
}

.chat-files {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e0e0e0;
}

.file-item {
  width: 100%;
  /* justify-content: space-between; */
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  color: #2e7d32;
  cursor: pointer;
}

.file-item i {
  font-size: 1rem;
}

.file-name {
  font-size: 0.875rem;
}

.download-icon {
  margin-left: auto;
  color: #245B5F;
  font-size: 0.875rem;
}

.chat-files .file-item {
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.chat-files .file-item:hover {
  background-color: #f8f9fa;
}

.chat-input {
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  background: #fff;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.chat-input-box {
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.chat-input-box:focus-within {
  border-color: #245B5F;
  box-shadow: 0 0 0 2px rgba(36, 91, 95, 0.1);
}

.input-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.input-actions-left {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.input-actions-right {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.selected-files {
  margin-bottom: 0.5rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-loader {
  color: #666;
}

.new-message-container {
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.form-input:focus {
  border-color: #245B5F;
  box-shadow: 0 0 0 2px rgba(36, 91, 95, 0.1);
}

.form-input::placeholder {
  color: #999;
}

.form-actions {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
}

.send-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.empty-chat {
  margin-top: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-style: italic;
}

:deep(.p-fileupload-buttonbar) {
  padding: 0;
}

:deep(.p-fileupload-content) {
  display: none;
}

.messaging-sidebar .p-sidebar-header-content {
  width: 100%;
}

:global(.p-sidebar-content) {
  padding: 0 10.5px !important;
}

:deep(.p-button) {
  min-width: 100px;
}

:deep(.p-button-text) {
  color: #666;
}

:deep(.p-button-text:hover) {
  background: #f8f9fa;
}

:deep(.p-button:not(.p-button-text)) {
  background: #245B5F;
  border-color: #245B5F;
}

:deep(.p-button:not(.p-button-text)):hover {
  background: #1a3f42;
  border-color: #1a3f42;
}

:deep(.p-button:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.loading-state i,
.error-state i,
.empty-state i {
  font-size: 2rem;
}

.error-state {
  color: #dc3545;
}

.empty-state {
  color: #6c757d;
}

.empty-state .p-button {
  margin-top: 1rem;
}

.remove-file-button {
  width: 2rem !important;
  min-width: 2rem !important;
  height: 2rem !important;
}

.messages-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  background-color: #FCFCFC;
}

.messages-loading-state i {
  font-size: 2rem;
  color: #245B5F;
}

.messages-loading-state span {
  font-size: 1rem;
  color: #666;
}

.file-upload-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.file-upload-section .selected-files {
  margin-bottom: 0;
}

.file-upload-section .p-fileupload {
  border: 2px dashed #e0e0e0;
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  background: #f9f9f9;
  transition: all 0.2s ease;
}

.file-upload-section .p-fileupload:hover {
  border-color: #245B5F;
  background: #f5f8f8;
}

:deep(.p-fileupload-buttonbar) {
  background: transparent;
  border: none;
  padding: 0;
  justify-content: center;
}

:deep(.p-fileupload-choose-button) {
  background: #245B5F;
  border-color: #245B5F;
  color: white;
}

:deep(.p-fileupload-choose-button:hover) {
  background: #1a3f42;
  border-color: #1a3f42;
}

/* Quill Editor in Chat Input */
.chat-input .chat-input-box {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.chat-input .chat-input-box:focus-within {
  border-color: #245B5F;
  box-shadow: 0 0 0 2px rgba(36, 91, 95, 0.1);
}

:deep(.chat-input .p-quill-editor) {
  border: none;
  border-radius: 0;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-content) {
  min-height: 80px;
  max-height: 120px;
  overflow-y: auto;
  padding: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.4;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar) {
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  padding: 0.5rem;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button) {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  padding: 0;
  margin: 0 0.125rem;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button i) {
  font-size: 0.875rem;
}

/* Compact toolbar for chat input */
:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button:not(.p-button-icon-only)) {
  display: none;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button.p-button-icon-only) {
  background: transparent;
  border: 1px solid transparent;
  color: #666;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button.p-button-icon-only:hover) {
  background: #e9ecef;
  border-color: #dee2e6;
  color: #333;
}

:deep(.chat-input .p-quill-editor .p-quill-editor-toolbar .p-button.p-button-icon-only.p-highlight) {
  background: #245B5F;
  border-color: #245B5F;
  color: white;
}

</style>

